import React, { useState } from 'react';
import { Upload, FileText, X, CheckCircle } from 'lucide-react';
import { useMyContext } from '../../../context/MyContext';
import * as Papa from 'papaparse';
import { Base_Url, uploadPincode } from '../../../config/config';
import toast from 'react-hot-toast';
import { FiEye } from 'react-icons/fi';
import { FiDownload } from 'react-icons/fi';
import NavBar from '../../../component/b2c/navBar/navBar';
import Sidebar from '../../../component/b2c/sidebar/sidebar';
import FullPageLoader from '../../../component/fullPageLoader/fullPageLoader';

export default function UploadPincodeComponent() {
  const [file, setFile] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPartner, setSelectedPartner] = useState('bluedart');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const token = localStorage.getItem('token');
  const cus_id = localStorage.getItem('cus_id');

  const partners = [
    { value: 'bluedart', label: 'BlueDart', icon: '🚚' },
    { value: 'delhivery', label: 'Delhivery', icon: '🚚' },
    { value: 'ecom', label: 'Ecom Express', icon: '🚚' },
    { value: 'ekart', label: 'Ekart', icon: '🚚' },
    { value: 'xpressbees', label: 'XpressBees', icon: '🚚' }
  ];

  const API_CONFIG = {
    endpoint: Base_Url + uploadPincode,
    cus_id: cus_id,
    token: token
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError('');
    setSuccess('');

    if (selectedFile && selectedFile.type === 'text/csv') {
      Papa.parse(selectedFile, {
        complete: (results) => {
          if (results.data && results.data.length > 0) {
            const headers = results.data[0];
            const dataRows = results.data.slice(1).filter(row => row.some(cell => cell.trim() !== ''));

            const parsedData = dataRows.map(row => {
              const obj = {};
              headers.forEach((header, index) => {
                obj[header] = row[index] || '';
              });
              return obj;
            });

            setPreviewData(parsedData);
          }
        },
        header: false,
        skipEmptyLines: true,
        error: (error) => {
          setError('Error parsing CSV file: ' + error.message);
        }
      });
    }
  };

  const openPreviewModal = () => {
    if (previewData && previewData.length > 0) {
      setShowPreview(true);
    } else {
      setError('No valid data to preview');
    }
  };

  const closePreviewModal = () => {
    setShowPreview(false);
  };

  const handlePartnerSelect = (partner) => {
    setSelectedPartner(partner.value);
    setIsDropdownOpen(false);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      toast.error('Please select a file first');
      return;
    }
    if (!previewData || previewData.length === 0) {
      setError('No valid data to upload');
      toast.error('No valid data to upload');
      return;
    }
    setIsUploading(true);
    setError('');
    setSuccess('');
    setIsPageLoading(true);
    try {
      const formData = new FormData();
      formData.append('csv_file', file);
      formData.append('partner', selectedPartner);

      const response = await fetch(API_CONFIG.endpoint, {
        method: 'POST',
        headers: {
          'cus_id': API_CONFIG.cus_id,
          'token': API_CONFIG.token,
        },
        body: formData,
      });
      const result = await response.json();

      if (response.ok) {
        setSuccess('File uploaded successfully!');
        toast.success('File uploaded successfully!');
        setFile(null);
        setPreviewData(null);
        setShowPreview(false);
        document.querySelector('input[type="file"]').value = '';
        setIsPageLoading(false);
      } else {
        const message = result?.messages?.csv_file || result?.message || 'Upload failed';
        setError(message);
        toast.error(message);
        setIsPageLoading(false);
      }
    } catch (error) {
      const message = error?.message || 'Upload failed';
      setError('Upload failed: ' + message);
      toast.error('Upload failed: ' + message);
      setIsPageLoading(false);
    } finally {
      setIsUploading(false);
      setIsPageLoading(false);
    }
  };

  // Custom Dropdown Component
  const PartnerDropdown = () => {
    const selectedPartnerObj = partners.find(p => p.value === selectedPartner);

    return (
      <div className="relative">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
          Select Delivery Partner
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="relative w-full bg-white dark:bg-black border border-gray-300 dark:border-gray-700 rounded pl-3 pr-9 py-2 text-left cursor-pointer transition-colors focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white focus:border-black dark:focus:border-white"
          >
            <span className="flex items-center">
              <span className="text-sm mr-2">{selectedPartnerObj?.icon}</span>
              <span className="block truncate text-black dark:text-white font-medium text-xs">
                {selectedPartnerObj?.label}
              </span>
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg
                className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </span>
          </button>

          {isDropdownOpen && (
            <div className="absolute z-50 mt-1 w-full bg-white dark:bg-black shadow-lg rounded border border-gray-200 dark:border-gray-800">
              <div className="py-1">
                {partners.map((partner) => (
                  <button
                    key={partner.value}
                    type="button"
                    onClick={() => handlePartnerSelect(partner)}
                    className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center hover:bg-gray-50 dark:hover:bg-gray-900 ${selectedPartner === partner.value
                      ? 'bg-gray-100 dark:bg-gray-900 text-black dark:text-white'
                      : 'text-gray-700 dark:text-gray-300'
                      }`}
                  >
                    <span className="text-sm mr-2">{partner.icon}</span>
                    <span className="font-medium">{partner.label}</span>
                    {selectedPartner === partner.value && (
                      <span className="ml-auto">
                        <CheckCircle className="h-3.5 w-3.5 text-black dark:text-white" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Preview Modal Component
  const PreviewModal = () => {
    if (!previewData || previewData.length === 0) return null;

    const headers = Object.keys(previewData[0]);
    const selectedPartnerObj = partners.find(p => p.value === selectedPartner);

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="bg-white dark:bg-black rounded-md shadow-2xl w-11/12 max-w-5xl max-h-[85vh] overflow-hidden border border-gray-200 dark:border-gray-800 flex flex-col">

          {/* Header */}
          <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-slate-800 shrink-0">
            <div className="flex items-center space-x-2">
              <FileText className="w-4 h-4 text-white" />
              <div>
                <h2 className="text-sm font-semibold text-white">
                  Pincode Data Preview
                </h2>
                <p className="text-gray-300 text-[11px] flex items-center gap-1">
                  {selectedPartnerObj?.icon} {selectedPartnerObj?.label}
                </p>
              </div>
            </div>

            <button
              onClick={closePreviewModal}
              className="p-1 hover:opacity-70 transition-opacity text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content Area */}
          <div className="overflow-auto flex-1 p-3">

            {/* Data Stats */}
            <div className="mb-3 bg-white dark:bg-black rounded border border-gray-200 dark:border-gray-800 p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Total Records: <span className="font-semibold text-black dark:text-white">{previewData.length}</span> ·
                  Columns: <span className="font-semibold text-black dark:text-white">{headers.length}</span> ·
                  Partner: <span className="font-semibold text-black dark:text-white">{selectedPartnerObj?.label}</span>
                </p>
                <p className="text-[11px] text-gray-400">Showing first 50 rows</p>
              </div>
            </div>

            {/* Table */}
            <div className="rounded border border-gray-200 dark:border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-slate-800">
                    <tr>
                      {headers.map((header, index) => (
                        <th
                          key={index}
                          className="p-2 text-left font-medium text-white text-[10px] uppercase tracking-wide border-r border-white/10 last:border-r-0"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {previewData.slice(0, 50).map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className="hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                      >
                        {headers.map((header, colIndex) => (
                          <td
                            key={colIndex}
                            className="p-2 text-xs text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-gray-800 last:border-r-0"
                          >
                            <div className="truncate max-w-xs" title={row[header]}>
                              {row[header]}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer Info */}
            {previewData.length > 50 && (
              <div className="text-center mt-3 p-2 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-800">
                <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                  Showing first 50 rows of {previewData.length} total rows
                </span>
              </div>
            )}
          </div>

          {/* Action Footer */}
          <div className="p-3 bg-white dark:bg-black border-t border-gray-200 dark:border-gray-800 shrink-0">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-1.5 text-xs text-gray-600 dark:text-gray-400">
                <CheckCircle className="w-3.5 h-3.5 text-black dark:text-white" />
                <span>Data loaded for {selectedPartnerObj?.label}</span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={closePreviewModal}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-700 text-black dark:text-white rounded text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                >
                  Close Preview
                </button>
                <button
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="px-3 py-1.5 bg-slate-800 dark:bg-white text-white dark:text-black rounded text-xs font-medium hover:opacity-80 transition-all flex items-center space-x-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="w-3.5 h-3.5" />
                  <span>{isUploading ? 'Uploading...' : 'Proceed with Upload'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-black' : 'bg-white'}`}>
      {showPreview && <PreviewModal />}

      <FullPageLoader
        isLoading={isPageLoading}
        message="Processing your request..."
      />

      <div className="z-10">
        <NavBar />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div
            className={`fixed z-20 h-full w-64 md:relative duration-300 bg-white dark:bg-black dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20 border-r border-gray-200 dark:border-gray-800`}
            style={
              window.innerWidth < 768
                ? { top: '113px', height: 'calc(100vh - 115px)', overflowY: 'auto' }
                : {}
            }
          >
            <Sidebar />
          </div>
        )}
        <div className="flex-1 overflow-hidden bg-gray-100 dark:bg-black flex flex-col">
          <div className="flex-1 flex items-start justify-center p-4 overflow-hidden">
            <div className=" w-full">
              <div className="bg-white dark:bg-black border border-gray-200 dark:border-gray-800 rounded-md overflow-hidden">

                {/* Header */}
                <div className="bg-slate-800 px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <Upload className="w-3.5 h-3.5 text-white" />
                    <h2 className="text-xs font-semibold text-white">
                      Upload Pincode List
                    </h2>
                  </div>
                  <p className="text-gray-300 text-[11px] ml-5 mt-0.5">Upload and process your pincode CSV file</p>
                </div>

                {/* Success/Error Messages */}
                {error && (
                  <div className="mx-4 mt-3 p-2.5 bg-white dark:bg-black border border-black dark:border-white rounded">
                    <div className="flex items-center">
                      <X className="w-3.5 h-3.5 text-black dark:text-white mr-2" />
                      <p className="text-black dark:text-white text-xs font-medium">{error}</p>
                    </div>
                  </div>
                )}

                {success && (
                  <div className="mx-4 mt-3 p-2.5 bg-black dark:bg-white border border-black dark:border-white rounded">
                    <div className="flex items-center">
                      <CheckCircle className="w-3.5 h-3.5 text-white dark:text-black mr-2" />
                      <p className="text-white dark:text-black text-xs font-medium">{success}</p>
                    </div>
                  </div>
                )}

                {/* Partner Selection */}
                <div className='mt-3 mx-4'>
                  <PartnerDropdown />
                </div>

                {/* File Upload Section */}
                <div className="p-4">
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                      Upload Your CSV File
                    </label>

                    {/* Drop Zone */}
                    <div className="relative border border-dashed rounded p-4 transition-colors bg-white dark:bg-black hover:bg-gray-50 dark:hover:bg-gray-900 cursor-pointer border-gray-300 dark:border-gray-700 hover:border-black dark:hover:border-white">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />

                      <div className="text-center">
                        <div className="mx-auto w-8 h-8 bg-slate-800 dark:bg-white rounded-full flex items-center justify-center mb-2">
                          <Upload className="w-4 h-4 text-white dark:text-black" />
                        </div>
                        <p className="text-xs font-medium text-black dark:text-white mb-1">
                          Drop your CSV file here or click to browse
                        </p>
                        <p className="text-[11px] text-gray-400">
                          Supports CSV files up to 10MB
                        </p>

                        {file && (
                          <div className="mt-2 p-1.5 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-800">
                            <div className="flex items-center justify-center space-x-1.5">
                              <CheckCircle className="w-3.5 h-3.5 text-black dark:text-white" />
                              <span className="font-medium text-xs text-black dark:text-white">{file.name}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col md:flex-row gap-2 mt-3">
                      <button
                        type="button"
                        onClick={handleUpload}
                        disabled={!file || isUploading}
                        className={`flex-1 py-2 rounded font-medium text-xs transition-colors ${!file || isUploading
                          ? 'bg-gray-200 dark:bg-gray-800 cursor-not-allowed text-gray-400'
                          : 'bg-slate-800 dark:bg-white text-white dark:text-black hover:opacity-80'
                          }`}
                      >
                        <div className="flex items-center justify-center space-x-1.5">
                          {isUploading ? (
                            <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          ) : (
                            <Upload className="w-3.5 h-3.5" />
                          )}
                          <span>{isUploading ? 'Processing...' : 'Upload & Process'}</span>
                        </div>
                      </button>

                      {file && previewData && (
                        <button
                          type="button"
                          onClick={openPreviewModal}
                          className="flex-1 md:flex-none md:px-4 py-2 rounded font-medium text-xs border border-black dark:border-white text-black dark:text-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors"
                        >
                          <div className="flex items-center justify-center space-x-1.5">
                            <FiEye className="w-3.5 h-3.5" />
                            <span>Preview Data</span>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Template Download */}
                  <div className="mt-2">
                    <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 border border-gray-200 dark:border-gray-800">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center space-x-2">
                          <FileText className="w-3.5 h-3.5 text-black dark:text-white" />
                          <div>
                            <h3 className="font-medium text-black dark:text-white text-xs">Need a template?</h3>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">Download our pre-formatted CSV template</p>
                          </div>
                        </div>
                        <a
                          href="/sampleFile/pincodeSample.csv"
                          download="pincodeSample.csv"
                          className="px-3 py-1.5 bg-slate-800 dark:bg-white text-white dark:text-black rounded font-medium text-xs hover:opacity-80 transition-all flex items-center space-x-1.5"
                        >
                          <FiDownload className="w-3.5 h-3.5" />
                          <span>Download</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}